import React, { useState, useEffect } from "react";
import { X, Scissors } from "lucide-react";

export default function SplitLineModal({ isOpen, onClose, originalQty, onConfirm }) {
  const [currentQty, setCurrentQty] = useState("");
  const [newQty, setNewQty] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setCurrentQty(originalQty.toString());
      setNewQty("");
      setError("");
    }
  }, [isOpen, originalQty]);

  if (!isOpen) return null;

  const handleNewQtyChange = (val) => {
    setNewQty(val);
    if (val === "") {
      setCurrentQty(originalQty.toString());
      setError("");
      return;
    }

    const n = parseFloat(val);
    if (isNaN(n)) {
      setError("Please enter a valid number for the new line quantity.");
      return;
    }

    if (n <= 0) {
      setError("Quantity must be greater than 0.");
      return;
    }

    if (n >= originalQty) {
      setError(`New line quantity must be less than the original quantity (${originalQty}).`);
      return;
    }

    const c = originalQty - n;
    // Format to avoid floating point precision issues (e.g. 0.1 + 0.2 = 0.30000000000000004)
    setCurrentQty(parseFloat(c.toFixed(4)).toString());
    setError("");
  };

  const handleCurrentQtyChange = (val) => {
    setCurrentQty(val);
    if (val === "") {
      setNewQty("");
      setError("");
      return;
    }

    const c = parseFloat(val);
    if (isNaN(c)) {
      setError("Please enter a valid number for the current line quantity.");
      return;
    }

    if (c <= 0) {
      setError("Quantity must be greater than 0.");
      return;
    }

    if (c >= originalQty) {
      setError(`Current line quantity must be less than the original quantity (${originalQty}).`);
      return;
    }

    const n = originalQty - c;
    setNewQty(parseFloat(n.toFixed(4)).toString());
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const cVal = parseFloat(currentQty);
    const nVal = parseFloat(newQty);

    if (isNaN(cVal) || cVal <= 0) {
      setError("Please enter a valid positive number for current line quantity.");
      return;
    }

    if (isNaN(nVal) || nVal <= 0) {
      setError("Please enter a valid positive number for new line quantity.");
      return;
    }

    // Check sum is equal to original quantity (within floating point precision)
    const sum = cVal + nVal;
    if (Math.abs(sum - originalQty) > 0.0001) {
      setError(`The sum of split quantities (${sum}) must equal the original quantity (${originalQty}).`);
      return;
    }

    setError("");
    onConfirm(cVal, nVal);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: "400px" }}>
        <div className="modal-header">
          <h3 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Scissors size={20} className="text-primary" /> Split Line Item
          </h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "1.5rem" }}>
          <div style={{ marginBottom: "1.25rem", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
            Original Quantity: <strong>{originalQty}</strong>
          </div>

          {error && (
            <div className="alert alert-danger" style={{ marginBottom: "1.25rem", padding: "0.5rem 0.75rem", fontSize: "0.85rem" }}>
              {error}
            </div>
          )}

          <div className="form-group" style={{ marginBottom: "1rem" }}>
            <label className="form-label" htmlFor="currentQty">
              Current Line Quantity
            </label>
            <input
              id="currentQty"
              type="number"
              step="any"
              className="form-input"
              style={{ padding: "0.5rem 0.75rem" }}
              value={currentQty}
              onChange={(e) => handleCurrentQtyChange(e.target.value)}
              placeholder="Prefilled QTY..."
              required
            />
          </div>

          <div className="form-group" style={{ marginBottom: "1.5rem" }}>
            <label className="form-label" htmlFor="newQty">
              New Line Quantity
            </label>
            <input
              id="newQty"
              type="number"
              step="any"
              className="form-input"
              style={{ padding: "0.5rem 0.75rem" }}
              value={newQty}
              onChange={(e) => handleNewQtyChange(e.target.value)}
              placeholder="Quantity for split line..."
              required
            />
          </div>

          <div className="modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={!!error}>
              OK
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
